import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { BASE as API_BASE } from '../services/api';
import { Button, Card, Input, Spinner } from '../components/ui';
import { BrandContext } from '../context/BrandContext';

const DEFAULT_ASSET_LIMIT = 50;
const DEFAULT_VIDEO_LIMIT = 50;

function resolveMaybeRelativeUrl(url) {
  try {
    const s = String(url || '').trim();
    if (!s) return '';
    if (/^https?:\/\//i.test(s)) return s;
    if (s.startsWith('/uploads/')) return `${String(API_BASE || '').replace(/\/$/, '')}${s}`;
    return s;
  } catch {
    return '';
  }
}

function ProgressBar({ value }) {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div style={{ background: 'rgba(55,65,81,0.7)', borderRadius: 999, height: 8, overflow: 'hidden' }}>
      <div
        style={{
          width: `${pct}%`,
          height: '100%',
          background: 'linear-gradient(90deg, rgba(255, 90, 31,1) 0%, rgba(255, 90, 31,1) 55%, rgba(59,130,246,1) 100%)',
          transition: 'width 250ms ease'
        }}
      />
    </div>
  );
}

function getAssetLabel(asset, fallback) {
  if (!asset) return fallback;
  return (
    asset.name ||
    asset.filename ||
    (asset.url ? decodeURIComponent(asset.url.split('/').pop() || '') : fallback)
  );
}

function formatAssetTimestamp(asset) {
  const ts = asset?.createdAt || asset?.created_at;
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return '';
  }
}

export default function QuickStart() {
  const navigate = useNavigate();
  const { profile: brandProfile, profileExists } = React.useContext(BrandContext);
  const [projects, setProjects] = React.useState([]);
  const [projectId, setProjectId] = React.useState('');
  const [newProjectName, setNewProjectName] = React.useState('');
  const [contextText, setContextText] = React.useState('');

  const [assets, setAssets] = React.useState({ audio: [], images: [], videos: [] });

  const [aspectRatio, setAspectRatio] = React.useState('9:16');
  const [durationSec, setDurationSec] = React.useState(8);

  const [busy, setBusy] = React.useState({
    loading: false,
    creatingProject: false,
    savingContext: false,
    generatingAudio: false,
    generatingImage: false,
    startingVideo: false,
  });

  const [job, setJob] = React.useState({
    jobId: '',
    status: '',
    progress: 0,
    error: '',
    videoUrl: '',
    predictionId: ''
  });

  const jobPollRef = React.useRef(null);

  const setBusyKey = React.useCallback((key, val) => {
    setBusy((b) => ({ ...b, [key]: !!val }));
  }, []);

  const latestAudio = assets.audio?.[0] || null;
  const latestImage = assets.images?.[0] || null;
  const latestVideo = assets.videos?.[0] || null;

  const loadProjects = React.useCallback(async () => {
    setBusyKey('loading', true);
    try {
      const res = await api.composerListProjects();
      const items = Array.isArray(res?.items) ? res.items : [];
      setProjects(items);
      if (!projectId && items.length) {
        setProjectId(String(items[0].id || ''));
      }
    } catch {
      setProjects([]);
    } finally {
      setBusyKey('loading', false);
    }
  }, [projectId, setBusyKey]);

  const loadAssets = React.useCallback(async (pid) => {
    const id = String(pid || '').trim();
    if (!id) return;
    try {
      const query = new URLSearchParams({
        limit: String(DEFAULT_ASSET_LIMIT),
        videosLimit: String(DEFAULT_VIDEO_LIMIT)
      });
      const res = await api.get(`/api/composer/projects/${encodeURIComponent(id)}/assets?${query.toString()}`);
      const items = res?.items || {};
      const normAudio = Array.isArray(items.audio)
        ? items.audio.map((a) => ({
            ...a,
            url: resolveMaybeRelativeUrl(a?.url || a?.path || a?.audioPath || ''),
            waveformUrl: resolveMaybeRelativeUrl(a?.waveformUrl || a?.waveform_url || ''),
            thumbnailUrl: resolveMaybeRelativeUrl(a?.thumbnailUrl || a?.thumbnail_url || ''),
          }))
        : [];
      const normImages = Array.isArray(items.images)
        ? items.images.map((im) => ({
            ...im,
            url: resolveMaybeRelativeUrl(im?.url || ''),
            thumbnailUrl: resolveMaybeRelativeUrl(im?.thumbnailUrl || im?.thumbnail_url || im?.url || ''),
          }))
        : [];
      const normVideos = Array.isArray(items.videos)
        ? items.videos.map((v) => ({
            ...v,
            url: resolveMaybeRelativeUrl(v?.url || ''),
            thumbnailUrl: resolveMaybeRelativeUrl(v?.thumbnailUrl || v?.thumbnail_url || v?.url || ''),
          }))
        : [];
      setAssets({
        audio: normAudio,
        images: normImages,
        videos: normVideos,
      });
    } catch {
      setAssets({ audio: [], images: [], videos: [] });
    }
  }, []);

  React.useEffect(() => {
    loadProjects();
    return () => {
      if (jobPollRef.current) {
        try { clearInterval(jobPollRef.current); } catch {}
        jobPollRef.current = null;
      }
    };
  }, [loadProjects]);

  React.useEffect(() => {
    if (projectId) {
      loadAssets(projectId);
      (async () => {
        try {
          const p = await api.composerGetProject(projectId);
          const ctx = String(p?.item?.context || '').trim();
          if (ctx) setContextText(ctx);
        } catch {}
      })();
    }
  }, [projectId, loadAssets]);

  React.useEffect(() => {
    if (profileExists && brandProfile?.name && !newProjectName) {
      setNewProjectName(brandProfile.name);
    }
  }, [brandProfile?.name, newProjectName, profileExists]);

  React.useEffect(() => {
    if (profileExists && brandProfile?.mission && !contextText.trim()) {
      setContextText(brandProfile.mission);
    }
  }, [brandProfile?.mission, contextText, profileExists]);

  const ensureContextSaved = React.useCallback(async () => {
    const pid = String(projectId || '').trim();
    const ctx = String(contextText || '').trim();
    if (!pid) throw new Error('project_required');
    if (!ctx) throw new Error('context_required');
    setBusyKey('savingContext', true);
    try {
      const res = await api.composerUpdateProject(pid, { context: ctx });
      if (res?.error) throw new Error(res.error);
      return true;
    } finally {
      setBusyKey('savingContext', false);
    }
  }, [projectId, contextText, setBusyKey]);

  const onCreateProject = React.useCallback(async () => {
    const name = String(newProjectName || '').trim();
    if (!name) return;
    setBusyKey('creatingProject', true);
    try {
      const payload = { name };
      const ctx = String(contextText || '').trim();
      if (ctx) payload.context = ctx;
      const res = await api.composerCreateProject(payload);
      if (res?.error) throw new Error(res.error);
      const id = String(res?.item?.id || res?.id || '').trim();
      await loadProjects();
      if (id) setProjectId(id);
      setNewProjectName('');
    } finally {
      setBusyKey('creatingProject', false);
    }
  }, [newProjectName, contextText, loadProjects, setBusyKey]);

  const onGenerateAudio = React.useCallback(async () => {
    setBusyKey('generatingAudio', true);
    try {
      await ensureContextSaved();
      const res = await api.composerCreateAudio(projectId, {});
      if (res?.error) throw new Error(res.error);
      await loadAssets(projectId);
    } finally {
      setBusyKey('generatingAudio', false);
    }
  }, [ensureContextSaved, projectId, loadAssets, setBusyKey]);

  const onGenerateImage = React.useCallback(async () => {
    setBusyKey('generatingImage', true);
    try {
      await ensureContextSaved();
      const res = await api.composerCreateImage(projectId, {});
      if (res?.error) throw new Error(res.error);
      await loadAssets(projectId);
    } finally {
      setBusyKey('generatingImage', false);
    }
  }, [ensureContextSaved, projectId, loadAssets, setBusyKey]);

  const stopJobPolling = React.useCallback(() => {
    if (jobPollRef.current) {
      try { clearInterval(jobPollRef.current); } catch {}
      jobPollRef.current = null;
    }
  }, []);

  const startJobPolling = React.useCallback((jobId) => {
    stopJobPolling();
    const id = String(jobId || '').trim();
    if (!id) return;
    jobPollRef.current = setInterval(async () => {
      try {
        const snap = await api.getJob(id);
        const status = String(snap?.status || '').toLowerCase();
        const progress = Number(snap?.progress || 0);
        const error = snap?.error ? String(snap.error) : '';
        const result = snap?.result || null;
        const videoUrl = resolveMaybeRelativeUrl(result?.asset?.url || result?.asset?.videoUrl || result?.videoUrl || '');

        setJob((j) => ({
          ...j,
          status: status || j.status,
          progress: Number.isFinite(progress) ? progress : j.progress,
          error,
          videoUrl: videoUrl || j.videoUrl,
        }));

        if (status === 'done' || status === 'error') {
          stopJobPolling();
          await loadAssets(projectId);
        }
      } catch {
        // keep polling
      }
    }, 2000);
  }, [loadAssets, projectId, stopJobPolling]);

  const onStartVideo = React.useCallback(async () => {
    const pid = String(projectId || '').trim();
    if (!pid) return;

    setBusyKey('startingVideo', true);
    try {
      await ensureContextSaved();
      await loadAssets(pid);

      const latestAudio = (assets.audio || [])[0];
      const latestImage = (assets.images || [])[0];
      const audioAssetId = String(latestAudio?.id || latestAudio?.assetId || latestAudio?._id || '').trim();
      const imageAssetId = String(latestImage?.id || latestImage?.assetId || latestImage?._id || '').trim();

      if (!audioAssetId) throw new Error('audio_asset_required');
      if (!imageAssetId) throw new Error('image_asset_required');

      setJob({ jobId: '', status: 'starting', progress: 0, error: '', videoUrl: '', predictionId: '' });

      const resp = await api.composerVideoStart(pid, {
        audioAssetId,
        imageAssetId,
        durationSec,
        aspectRatio,
        template: 'animated'
      });

      if (!resp?.ok) throw new Error(resp?.error || 'video_start_failed');

      const predictionId = String(resp.predictionId || '').trim();
      const jobId = String(resp.jobId || '').trim();

      setJob((j) => ({ ...j, predictionId, jobId, status: jobId ? 'queued' : 'starting' }));

      if (jobId) {
        startJobPolling(jobId);
        return;
      }

      // Fallback: no queue job id. Let user open Project Details to continue.
      await loadAssets(pid);

    } finally {
      setBusyKey('startingVideo', false);
    }
  }, [assets.audio, assets.images, aspectRatio, durationSec, ensureContextSaved, loadAssets, projectId, setBusyKey, startJobPolling]);

  const isWorking = Object.values(busy).some(Boolean);

  return (
    <main style={{ minHeight: '100vh', background: 'var(--gradient-hero)' }}>
      <div className="container" style={{ padding: '24px 0 64px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
          <div style={{ display: 'grid', gap: 4 }}>
            <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: -0.5, lineHeight: 1.1 }}>
              Quick Start
            </div>
            <div className="muted" style={{ maxWidth: 760 }}>
              Go from idea to assets in three clicks: set context, make a voice, make a visual. Video is optional.
            </div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button variant="secondary" onClick={() => loadProjects()} disabled={busy.loading}>
              {busy.loading ? 'Refreshing…' : 'Refresh'}
            </Button>
            {!!projectId && (
              <Button variant="outline" onClick={() => navigate(`/projects/${encodeURIComponent(String(projectId))}`)}>
                Open Project
              </Button>
            )}
          </div>
        </div>

        {profileExists && (
          <Card hover style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
              <div style={{ flex: 1, minWidth: 240 }}>
                <p style={{ margin: 0, fontSize: 12, textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>Brand profile active</p>
                <h3 style={{ margin: '4px 0 8px' }}>{brandProfile?.name || 'Untitled brand'}</h3>
                <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>{brandProfile?.tagline || 'Update your tagline to inspire scripts.'}</p>
              </div>
              <div style={{ display: 'flex', gap: 12, minWidth: 200 }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>Primary</span>
                  <div style={{ background: brandProfile?.primaryColor || '#ff5a1f', borderRadius: 6, height: 40, marginTop: 4 }} />
                </div>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>Secondary</span>
                  <div style={{ background: brandProfile?.secondaryColor || '#ff5a1f', borderRadius: 6, height: 40, marginTop: 4 }} />
                </div>
              </div>
              <Button variant="ghost" onClick={() => navigate('/brand')}>
                Edit brand →
              </Button>
            </div>
          </Card>
        )}

        {/* Latest asset summary */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
          {latestAudio ? (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: 'rgba(31,41,55,0.8)',
                border: '1px solid #374151',
                borderRadius: 999,
                padding: '6px 10px',
                color: '#e5e7eb',
                fontSize: 12
              }}
            >
              🎧 Voice: {decodeURIComponent(latestAudio.url.split('/').pop() || '')}
            </span>
          ) : null}
          {latestImage ? (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: 'rgba(31,41,55,0.8)',
                border: '1px solid #374151',
                borderRadius: 999,
                padding: '6px 10px',
                color: '#e5e7eb',
                fontSize: 12
              }}
            >
              🖼️ Image ready
            </span>
          ) : null}
          {latestVideo ? (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: 'rgba(31,41,55,0.8)',
                border: '1px solid #374151',
                borderRadius: 999,
                padding: '6px 10px',
                color: '#e5e7eb',
                fontSize: 12
              }}
            >
              🎬 Video ready
            </span>
          ) : null}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
          <Card title="Project" subtitle="Pick an existing project or name a new one." hover>
            <div style={{ display: 'grid', gap: 12 }}>
              <div style={{ display: 'grid', gap: 6 }}>
                <div className="muted" style={{ fontSize: 12 }}>Existing projects</div>
                <select
                  className="select"
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                >
                  {projects.map((p) => (
                    <option key={p.id} value={String(p.id)}>
                      {p.name || p.id}
                    </option>
                  ))}
                </select>
                {!projects.length && (
                  <div className="muted" style={{ fontSize: 12 }}>
                    No projects found. Create one below.
                  </div>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'end' }}>
                <Input
                  label="New project name"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder="e.g. Holiday launch for Acme"
                />
                <Button
                  onClick={onCreateProject}
                  disabled={!String(newProjectName || '').trim() || busy.creatingProject}
                >
                  {busy.creatingProject ? 'Creating…' : 'Create'}
                </Button>
              </div>
            </div>
          </Card>

          <Card title="Context" subtitle="Short, specific text powers every asset." hover>
            <div style={{ display: 'grid', gap: 10 }}>
              <textarea
                value={contextText}
                onChange={(e) => setContextText(e.target.value)}
                rows={6}
                placeholder="What are you making? Who is it for? What is the offer? Tone? Call-to-action?"
                className="input"
                style={{ width: '100%', resize: 'vertical' }}
              />
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button
                  onClick={async () => {
                    try {
                      await ensureContextSaved();
                    } catch (e) {
                      // handled by toast inside api layer
                    }
                  }}
                  disabled={busy.savingContext || !projectId || !String(contextText || '').trim()}
                >
                  {busy.savingContext ? 'Saving…' : 'Save Context'}
                </Button>
                <div className="muted" style={{ fontSize: 12 }}>
                  You need context + at least one voice + one image before starting video.
                </div>
              </div>
            </div>
          </Card>

          <Card title="Generate assets" subtitle="Create a voice and an image. Video stays optional." hover>
            <div style={{ display: 'grid', gap: 14 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
                <div className="section" style={{ background: 'rgba(17,24,39,0.8)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ fontWeight: 700 }}>Audio</div>
                    {busy.generatingAudio && <Spinner size="sm" />}
                  </div>
                  <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                    Voiceover generated from your context.
                  </div>
                  <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <Button onClick={onGenerateAudio} disabled={!projectId || busy.generatingAudio}>
                      {busy.generatingAudio ? 'Generating…' : 'Generate Audio'}
                    </Button>
                    {!!assets.audio.length && (
                      <Button variant="secondary" onClick={() => loadAssets(projectId)}>
                        Refresh
                      </Button>
                    )}
                  </div>
                  {!!assets.audio.length && (
                    <div style={{ marginTop: 10 }}>
                      <audio
                        controls
                        preload="metadata"
                        style={{ width: '100%' }}
                        src={assets.audio[0]?.url}
                        data-testid="quickstart-audio-latest"
                      />
                    </div>
                  )}
                </div>

                <div className="section" style={{ background: 'rgba(17,24,39,0.8)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ fontWeight: 700 }}>Image</div>
                    {busy.generatingImage && <Spinner size="sm" />}
                  </div>
                  <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                    Visual generated from your context.
                  </div>
                  <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <Button onClick={onGenerateImage} disabled={!projectId || busy.generatingImage}>
                      {busy.generatingImage ? 'Generating…' : 'Generate Image'}
                    </Button>
                    {!!assets.images.length && (
                      <Button variant="secondary" onClick={() => loadAssets(projectId)}>
                        Refresh
                      </Button>
                    )}
                  </div>
                  {!!assets.images.length && (() => {
                    const img = assets.images[0] || {};
                    const rawSrc = img.thumbnailUrl || img.thumbnail_url || img.url;
                    const src = resolveMaybeRelativeUrl(rawSrc);
                    return (
                      <div style={{ marginTop: 10 }}>
                        <img
                          alt="latest"
                          src={src}
                          style={{ width: '100%', borderRadius: 12, border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-2)' }}
                          loading="lazy"
                        />
                      </div>
                    );
                  })()}
                </div>
              </div>

              <div className="section" style={{ background: 'rgba(17,24,39,0.85)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ display: 'grid', gap: 4 }}>
                    <div style={{ fontWeight: 800 }}>Video</div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      Starts a background job when queue is enabled.
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                    <label className="muted" style={{ fontSize: 12 }}>
                      Aspect
                      <select
                        className="select"
                        value={aspectRatio}
                        onChange={(e) => setAspectRatio(e.target.value)}
                        style={{ marginLeft: 8 }}
                      >
                        <option value="9:16">9:16</option>
                        <option value="16:9">16:9</option>
                        <option value="1:1">1:1</option>
                      </select>
                    </label>
                    <label className="muted" style={{ fontSize: 12 }}>
                      Duration
                      <select
                        className="select"
                        value={String(durationSec)}
                        onChange={(e) => setDurationSec(Number(e.target.value) || 8)}
                        style={{ marginLeft: 8 }}
                      >
                        <option value="4">4s</option>
                        <option value="6">6s</option>
                        <option value="8">8s</option>
                      </select>
                    </label>
                    <Button
                      onClick={onStartVideo}
                      disabled={!projectId || busy.startingVideo || !assets.audio.length || !assets.images.length}
                    >
                      {busy.startingVideo ? 'Starting…' : 'Start Video'}
                    </Button>
                  </div>
                </div>

                {(job.jobId || job.predictionId || job.status || job.error) && (
                  <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      <div className="muted" style={{ fontSize: 12 }}>
                        {job.jobId ? (
                          <span>
                            Job: <code>{job.jobId}</code>
                          </span>
                        ) : null}
                        {job.predictionId ? (
                          <span style={{ marginLeft: 10 }}>
                            Prediction: <code>{job.predictionId}</code>
                          </span>
                        ) : null}
                      </div>
                      {job.status ? <div className="muted" style={{ fontSize: 12 }}>Status: {job.status}</div> : null}
                    </div>
                    <ProgressBar value={job.progress} />
                    {job.error ? (
                      <div style={{ color: 'var(--color-danger)', fontSize: 13 }}>
                        {job.error}
                      </div>
                    ) : null}
                    {job.videoUrl ? (
                      <div style={{ display: 'grid', gap: 8 }}>
                        <video
                          controls
                          playsInline
                          preload="metadata"
                          src={job.videoUrl}
                          style={{ width: '100%', borderRadius: 12, border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-2)' }}
                        />
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                          <a href={job.videoUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--color-primary)' }}>
                            Open
                          </a>
                          <a href={job.videoUrl} download style={{ color: 'var(--color-primary)' }}>
                            Download
                          </a>
                        </div>
                      </div>
                    ) : null}
                    {!job.jobId && job.predictionId && !job.videoUrl ? (
                      <div className="muted" style={{ fontSize: 12 }}>
                        Queue job ID was not returned. If queue is disabled, you can finalize from the project page.
                        <span style={{ marginLeft: 8 }}>
                          <Link to={`/projects/${encodeURIComponent(String(projectId))}`} style={{ color: 'var(--color-primary)' }}>
                            Go to Project →
                          </Link>
                        </span>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>

              <div className="section" style={{ background: 'rgba(17,24,39,0.85)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontWeight: 700 }}>Project Videos</div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {assets.videos.length ? `${assets.videos.length} video${assets.videos.length === 1 ? '' : 's'} generated in this project.` : 'Generate a video to see it here.'}
                    </div>
                  </div>
                  <Button variant="secondary" onClick={() => loadAssets(projectId)}>
                    Refresh
                  </Button>
                </div>
                {assets.videos.length === 0 ? (
                  <div style={{ marginTop: 16, border: '1px dashed rgba(226,232,240,0.2)', borderRadius: 12, padding: 20, color: '#9ca3af', fontSize: 13 }}>
                    No project videos yet. Generate one to kick off your campaign.
                  </div>
                ) : (
                  <div style={{ marginTop: 16, display: 'grid', gap: 16 }}>
                    {assets.videos.map((video, index) => (
                      <div
                        key={video.id || video.assetId || video._id || `video-${index}`}
                        style={{
                          border: '1px solid rgba(148,163,184,0.2)',
                          borderRadius: 16,
                          padding: 16,
                          background: 'rgba(15,23,42,0.65)',
                          boxShadow: '0 15px 35px rgba(15,23,42,0.35)'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                          <div>
                            <div style={{ fontWeight: 600 }}>{getAssetLabel(video, 'Project Video')}</div>
                            <div className="muted" style={{ fontSize: 12 }}>{formatAssetTimestamp(video)}</div>
                          </div>
                          {index === 0 ? (
                            <span style={{ background: 'rgba(16,185,129,0.15)', color: '#34d399', fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 999 }}>
                              Latest
                            </span>
                          ) : null}
                        </div>
                        <div style={{ marginTop: 12 }}>
                          <video
                            controls
                            playsInline
                            preload="metadata"
                            poster={resolveMaybeRelativeUrl(video.thumbnailUrl)}
                            src={resolveMaybeRelativeUrl(video.url)}
                            style={{ width: '100%', borderRadius: 12, border: '1px solid rgba(148,163,184,0.3)' }}
                          />
                        </div>
                        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                          <a
                            href={resolveMaybeRelativeUrl(video.url)}
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: '#93c5fd', fontSize: 13 }}
                          >
                            Open in new tab ↗
                          </a>
                          {video.durationMs ? (
                            <span className="muted" style={{ fontSize: 12 }}>
                              Duration: {(Number(video.durationMs) / 1000).toFixed(1)}s
                            </span>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Card>

          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            Tip: for fastest iteration, generate audio + image once, then only re-run video.
          </div>
        </div>

        <div style={{ marginTop: 18, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Button variant="ghost" onClick={() => navigate('/projects')} disabled={isWorking}>
            All Projects
          </Button>
          <Button variant="ghost" onClick={() => navigate('/videos')} disabled={isWorking}>
            Advanced Video Creator
          </Button>
        </div>
      </div>
    </main>
  );
}
